import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { usePersonaStore } from '@/stores/personaStore';
import { useFeatureFlag } from '@/stores/featureFlagsStore';
import { useUiStore } from '@/stores/uiStore';
import { functions } from '@/data/functions';
import { NavIcon, type NavIconKey } from './NavIcons';
import BevelButton from './BevelButton';
import CategorySection from './CategorySection';
import { CORE_ITEMS, ADVANCED_ITEMS, type SidebarItem } from './sidebarItems';
import styles from './Sidebar.module.css';

/**
 * Three-category nav rail — top-1% Apple-style bevel buttons grouped
 * under boxed category sections (Core / Functions / Advanced).
 *
 *  - Core      always-expanded (ZILM, Funding, Compliance, …)
 *  - Functions collapsed by default; click the header to expand. Once
 *              expanded the buttons behave just like Core's.
 *  - Advanced  always-expanded (Admin, Data, User, Tools, Labs, What's New,
 *              plus admin-only Feedback + Lineage).
 *
 * Each button's projected popover (right of the rail on hover/focus)
 * carries label + one-liner blurb. See sidebarItems.ts to edit copy.
 *
 * The Z logo at the top routes to /dashboard — replaces the previous
 * dedicated Home item, since the user is by definition already on a
 * page when they're using the rail.
 */
export function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const persona = usePersonaStore((s) => s.getCurrentPersona());
  const isPlatformAdmin = user?.roles?.includes('platform_admin') ?? false;
  const dataPaneV2 = useFeatureFlag('data_pane_v2');
  const functionsExpanded = useUiStore((s) => s.functionsExpanded);
  const toggleFunctionsExpanded = useUiStore((s) => s.toggleFunctionsExpanded);

  const isActive = (route: string) => {
    const [path, hash] = route.split('#');
    if (!location.pathname.startsWith(path)) return false;
    if (hash) return location.hash === `#${hash}`;
    if (path === '/admin') return location.hash === '' || location.hash === '#';
    return true;
  };

  const filterVisible = (items: readonly SidebarItem[]) =>
    items.filter((i) => {
      if (i.adminOnly && !isPlatformAdmin) return false;
      if (i.hideWhenDataPaneV2 && dataPaneV2) return false;
      return true;
    });

  const highlightedSet = new Set(persona.highlightedNavItems);
  const personaTint = `color-mix(in srgb, ${persona.color} 14%, transparent)`;

  const renderItem = (item: SidebarItem) => {
    const active = isActive(item.route);
    const highlighted = highlightedSet.has(item.label);
    return (
      <BevelButton
        key={item.id}
        icon={<NavIcon name={item.id} className={styles.icon} />}
        label={item.label}
        blurb={item.blurb}
        tone={item.tone}
        active={active}
        current={active}
        personaTint={highlighted && !active ? personaTint : undefined}
        ariaLabel={`${item.label} — ${item.blurb}`}
        onClick={() => navigate(item.route)}
      />
    );
  };

  const functionEntries = Object.values(functions);

  return (
    <nav className={styles.sidebar} aria-label="Main navigation">
      <button
        className={styles.logo}
        onClick={() => navigate('/dashboard')}
        aria-label="Go to Dashboard"
      >
        Z
      </button>

      <div className={styles.categories}>
        {/* Category 1 — Core (always expanded) */}
        <CategorySection label="Core" shade="core">
          {filterVisible(CORE_ITEMS).map(renderItem)}
        </CategorySection>

        {/* Category 2 — Functions (collapsed by default) */}
        <CategorySection
          label="Functions"
          shade="functions"
          collapsible={{
            expanded: functionsExpanded,
            onToggle: toggleFunctionsExpanded,
          }}
        >
          {functionEntries.map((fn) => {
            const route = `/functions/${fn.key}`;
            const active = isActive(route);
            const highlighted = highlightedSet.has(fn.name);
            return (
              <BevelButton
                key={fn.key}
                icon={<span aria-hidden="true">{fn.name.charAt(0)}</span>}
                label={fn.name}
                blurb={fn.shortDescription ?? `Open the ${fn.name} domain.`}
                tone="violet"
                active={active}
                current={active}
                personaTint={highlighted && !active ? personaTint : undefined}
                ariaLabel={`${fn.name} — ${fn.shortDescription ?? 'function domain'}`}
                onClick={() => navigate(route)}
              />
            );
          })}
        </CategorySection>

        {/* Category 3 — Advanced (always expanded) */}
        <CategorySection label="Advanced" shade="advanced">
          {filterVisible(ADVANCED_ITEMS).map(renderItem)}
        </CategorySection>
      </div>
    </nav>
  );
}

/** Re-export so legacy imports still work without churning each
 *  call site. NavIconKey is also re-exported from NavIcons.tsx. */
export type { NavIconKey };
